import type { ReactNode } from "react";
import { canRenderEditorialLink } from "../../../../shared/editorialLinkSafety";

export type ArticleBodyImage = {
  url: string;
  altText?: string | null;
  width?: number | null;
  height?: number | null;
  caption?: string | null;
  attribution?: string | null;
};

type ArticleBodyProps = {
  markdown: string;
  images?: ArticleBodyImage[];
  className?: string;
  publicInsightSlugs?: readonly string[];
};

function safeUrl(value: string, kind: "link" | "image"): string | null {
  const url = value.trim();
  if (!url || /^(?:\/|\\){2}/.test(url) || [...url].some((character) => character.charCodeAt(0) <= 32 || character.charCodeAt(0) === 127)) return null;

  if (/^(?:https?:)/i.test(url)) {
    try {
      const parsed = new URL(url);
      return parsed.protocol === "http:" || parsed.protocol === "https:" ? url : null;
    } catch {
      return null;
    }
  }

  if (kind === "link" && /^(?:mailto:|tel:)/i.test(url)) return url;
  if (/^[a-z][a-z\d+.-]*:/i.test(url)) return null;
  return url;
}

function inline(text: string, images: ArticleBodyImage[] = [], keyPrefix = "inline", publicInsightSlugs: readonly string[] = []): ReactNode[] {
  const tokens = /!\[([^\]]*)\]\(([^)\s]+)(?:\s+"([^"]*)")?\)|\[([^\]]+)\]\(([^)\s]+)(?:\s+"([^"]*)")?\)|(\*\*|__)(.+?)\7|(\*|_)(.+?)\9|`([^`]+)`/g;
  const nodes: ReactNode[] = [];
  let cursor = 0;
  let match: RegExpExecArray | null;
  let index = 0;

  while ((match = tokens.exec(text))) {
    if (match.index > cursor) nodes.push(text.slice(cursor, match.index));
    const key = `${keyPrefix}-${index++}`;
    if (match[1] !== undefined) {
      const src = safeUrl(match[2], "image");
      const metadata = images.find((image) => image.url === match![2]);
      const size = /^(?:width=)?(\d+)\s*[x, ]\s*(?:height=)?(\d+)$/i.exec(match[3] || "");
      if (src) {
        nodes.push(<img key={key} src={src} alt={match[1] || metadata?.altText || ""} width={metadata?.width || (size ? Number(size[1]) : undefined)} height={metadata?.height || (size ? Number(size[2]) : undefined)} title={match[3] && !size ? match[3] : undefined} loading="lazy" className="my-6 h-auto max-w-full rounded-lg" />);
      } else {
        nodes.push(match[1] || metadata?.altText || "Image");
      }
    } else if (match[4] !== undefined) {
      const safeHref = safeUrl(match[5], "link");
      const href = safeHref && canRenderEditorialLink(safeHref, publicInsightSlugs) ? safeHref : null;
      nodes.push(href
        ? <a key={key} href={href} title={match[6]} className="font-medium text-[#0d7078] underline underline-offset-4" rel={/^https?:/i.test(href) ? "noopener noreferrer" : undefined}>{match[4]}</a>
        : match[4]);
    } else if (match[7] !== undefined) {
      nodes.push(<strong key={key}>{match[8]}</strong>);
    } else if (match[9] !== undefined) {
      nodes.push(<em key={key}>{match[10]}</em>);
    } else {
      nodes.push(<code key={key} className="rounded bg-slate-100 px-1.5 py-0.5 text-[0.9em]">{match[11]}</code>);
    }
    cursor = tokens.lastIndex;
  }
  if (cursor < text.length) nodes.push(text.slice(cursor));
  return nodes;
}

function tableCells(line: string): string[] {
  const trimmed = line.trim().replace(/^\|/, "").replace(/\|$/, "");
  const cells: string[] = [];
  let current = "";
  let escaped = false;
  for (const char of trimmed) {
    if (char === "|" && !escaped) {
      cells.push(current.trim());
      current = "";
    } else {
      current += char;
    }
    escaped = char === "\\" && !escaped;
    if (char !== "\\") escaped = false;
  }
  cells.push(current.trim());
  return cells;
}

function isTableDivider(line: string): boolean {
  const cells = tableCells(line);
  return cells.length > 0 && cells.every((cell) => /^:?-{3,}:?$/.test(cell));
}

function isTable(lines: string[], index: number): boolean {
  return index + 1 < lines.length && lines[index].includes("|") && isTableDivider(lines[index + 1]);
}

export default function ArticleBody({ markdown, images = [], className = "", publicInsightSlugs = [] }: ArticleBodyProps) {
  const sourceLines = markdown.replace(/\r\n?/g, "\n").split("\n");
  const blocks: string[][] = [];
  let sourceIndex = 0;
  while (sourceIndex < sourceLines.length) {
    const current = sourceLines[sourceIndex].trim();
    if (!current) {
      sourceIndex += 1;
      continue;
    }
    const nextBlockStarts = (line: string, index: number) =>
      /^#{2,3}\s+/.test(line.trim()) ||
      /^[-*]\s+/.test(line.trim()) ||
      isTable(sourceLines, index);
    if (isTable(sourceLines, sourceIndex)) {
      const tableLines = [current, sourceLines[sourceIndex + 1].trim()];
      sourceIndex += 2;
      while (sourceIndex < sourceLines.length && sourceLines[sourceIndex].trim() && sourceLines[sourceIndex].includes("|")) {
        tableLines.push(sourceLines[sourceIndex].trim());
        sourceIndex += 1;
      }
      blocks.push(tableLines);
      continue;
    }
    if (/^#{2,3}\s+/.test(current)) {
      blocks.push([current]);
      sourceIndex += 1;
      continue;
    }
    if (/^[-*]\s+/.test(current)) {
      const listLines = [current];
      sourceIndex += 1;
      while (sourceIndex < sourceLines.length && /^[-*]\s+/.test(sourceLines[sourceIndex].trim())) {
        listLines.push(sourceLines[sourceIndex].trim());
        sourceIndex += 1;
      }
      blocks.push(listLines);
      continue;
    }
    const paragraph = [current];
    sourceIndex += 1;
    while (sourceIndex < sourceLines.length && sourceLines[sourceIndex].trim() && !nextBlockStarts(sourceLines[sourceIndex], sourceIndex)) {
      paragraph.push(sourceLines[sourceIndex].trim());
      sourceIndex += 1;
    }
    blocks.push(paragraph);
  }
  const rendered: ReactNode[] = [];

  for (let i = 0; i < blocks.length; i += 1) {
    const lines = blocks[i];
    if (!lines.length) continue;

    if (isTable(lines, 0)) {
      const headers = tableCells(lines[0]);
      const rows = lines.slice(2).filter((line) => line.includes("|")).map(tableCells);
      rendered.push(
        <div key={`table-${i}`} className="my-6 max-w-full overflow-x-auto overscroll-x-contain rounded-lg border border-slate-200">
          <table className="w-full min-w-max border-collapse text-left text-base">
            <thead className="bg-slate-50">
              <tr>{headers.map((header, column) => <th key={column} scope="col" className="border-b border-slate-200 px-4 py-3 font-semibold text-slate-900">{inline(header, images, `th-${i}-${column}`, publicInsightSlugs)}</th>)}</tr>
            </thead>
            <tbody>{rows.map((row, rowIndex) => <tr key={rowIndex} className="odd:bg-white even:bg-slate-50/70">{headers.map((_, column) => <td key={column} className="border-b border-slate-100 px-4 py-3 align-top text-slate-700">{inline(row[column] || "", images, `td-${i}-${rowIndex}-${column}`, publicInsightSlugs)}</td>)}</tr>)}</tbody>
          </table>
        </div>,
      );
      continue;
    }

    const heading = /^(#{2,6})\s+(.+)$/.exec(lines[0]);
    if (heading && lines.length === 1) {
      const content = inline(heading[2], images, `heading-${i}`, publicInsightSlugs);
      const Heading = `h${heading[1].length}` as "h2" | "h3" | "h4" | "h5" | "h6";
      const style = Heading === "h2" ? "pt-7 text-3xl font-bold tracking-tight text-slate-900"
        : Heading === "h3" ? "pt-4 text-xl font-bold text-slate-900" : "pt-4 text-lg font-bold text-slate-900";
      rendered.push(<Heading key={`heading-${i}`} className={style}>{content}</Heading>);
      continue;
    }

    if (lines.every((line) => /^[-*]\s+/.test(line))) {
      rendered.push(<ul key={`list-${i}`} className="my-5 list-disc space-y-2 pl-6">{lines.map((line, itemIndex) => <li key={itemIndex}>{inline(line.replace(/^[-*]\s+/, ""), images, `li-${i}-${itemIndex}`, publicInsightSlugs)}</li>)}</ul>);
      continue;
    }

    rendered.push(<p key={`paragraph-${i}`} className="my-5">{inline(lines.join(" "), images, `p-${i}`, publicInsightSlugs)}</p>);
  }

  return <div className={className}>{rendered}</div>;
}
